import { useCallback, useMemo, useRef, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
	aggridHeaderClass,
	commonNumberCellStyle,
} from "src/components/common/common";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";

import {
	defaultTQC1060_R01HeaderDto,
	TQC1060_R01HeaderDto,
} from "./Dto_TQC1060_R01";
import { useTQC1060_R01Store } from "./Store_TQC1060_R01";

const COLUMN_STATE_KEY = "TQC1060_R01-header-column-state";

interface Props {
	onHeaderRowClick: (row: TQC1060_R01HeaderDto) => void;
}

export const HeaderTable_TQC1060_R01 = ({ onHeaderRowClick }: Props) => {
	const {
		TQC1060_R01List,
		setTQC1060_R01List,
		selectedTQC1060_R01,
		initSelectedTQC1060_R01,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useTQC1060_R01Store();
	const gridRef = useRef<AgGridReact<TQC1060_R01HeaderDto>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);

	const { getRowId, createRowClassRules } =
		useGridSettingContent<TQC1060_R01HeaderDto>({
			gridRef,
			key: "valueIndex",
			gridData: TQC1060_R01List,
			defaultGridData: defaultTQC1060_R01HeaderDto,
			selectedRow: selectedTQC1060_R01,
			originalGridData: TQC1060_R01List,
			updateRow: () => undefined,
		});

	const columnDefs: ColDef<TQC1060_R01HeaderDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{ headerName: "사업장", field: "compNm", width: 110, headerClass: aggridHeaderClass },
			{
				headerName: "날짜",
				field: "transportDt",
				width: 110,
				headerClass: aggridHeaderClass,
				valueFormatter: ({ value }) => (value ? formatDate(new Date(value)) : ""),
			},
			{ headerName: "입고구분", field: "inoutClassNm", width: 110, headerClass: aggridHeaderClass },
			{ headerName: "거래처", field: "customCd", width: 100, headerClass: aggridHeaderClass },
			{ headerName: "상호", field: "customNm", width: 120, headerClass: aggridHeaderClass },
			{ headerName: "품목코드", field: "itemCd", width: 120, headerClass: aggridHeaderClass },
			{ headerName: "품명", field: "itemNm", width: 140, headerClass: aggridHeaderClass },
			{ headerName: "규격", field: "itemSpec", width: 110, headerClass: aggridHeaderClass },
			{
				headerName: "입고량",
				field: "qty",
				width: 90,
				headerClass: aggridHeaderClass,
				cellStyle: commonNumberCellStyle,
				valueFormatter: ({ value }) => (value == null ? "" : formatNumber(value)),
			},
			{ headerName: "단위", field: "unit", width: 70, headerClass: aggridHeaderClass },
			{ headerName: "LotNo", field: "lotNo", width: 130, headerClass: aggridHeaderClass },
			{
				headerName: "단가",
				field: "cost",
				width: 90,
				headerClass: aggridHeaderClass,
				cellStyle: commonNumberCellStyle,
				valueFormatter: ({ value }) => (value == null ? "" : formatNumber(value)),
			},
			{
				headerName: "검사여부",
				colId: "inspectStatus",
				width: 90,
				headerClass: aggridHeaderClass,
				valueGetter: ({ data, node }) =>
					node?.rowPinned ? "" : data?.inspectNo ? "검사완료" : "미검사",
			},
		],
		[],
	);

	const pinnedBottomRow = useMemo(() => {
		const totalQty = TQC1060_R01List.reduce(
			(sum, row) => sum + (Number(row.qty) || 0),
			0,
		);
		return [
			{
				valueIndex: "",
				rowIndex: "",
				compNm: `${TQC1060_R01List.length.toLocaleString()}건`,
				qty: totalQty,
				editable: false,
			},
		] as unknown as TQC1060_R01HeaderDto[];
	}, [TQC1060_R01List]);

	const rowClassRules = useMemo(
		() =>
			createRowClassRules<TQC1060_R01HeaderDto>({
				idKey: "valueIndex",
				selectedList: selectedTQC1060_R01?.valueIndex
					? [selectedTQC1060_R01.valueIndex]
					: [],
				deletedList: [],
			}),
		[createRowClassRules, selectedTQC1060_R01],
	);

	const handleRowClick = useCallback(
		(event: RowClickedEvent<TQC1060_R01HeaderDto>) => {
			if (event.node.rowPinned) return;
			if (event.data) onHeaderRowClick(event.data);
		},
		[onHeaderRowClick],
	);

	return (
		<GridContextWrapper<TQC1060_R01HeaderDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={TQC1060_R01List}
			setPaddedGridData={setTQC1060_R01List}
			initSelected={initSelectedTQC1060_R01}
			defaultRowData={defaultTQC1060_R01HeaderDto}
			allSelected={() => undefined}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Select<TQC1060_R01HeaderDto>
				ref={gridRef}
				rowData={TQC1060_R01List}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				setHoveredRowIndex={setHoveredRowIndex}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
